import React, { useEffect, useState } from 'react'

import { GraphQlCustomer } from '@/helpers/graphql'
import { IMeganav } from '@/types/Meganav'

import { useListrakCartTracking } from '@/hooks/useListrakProductTracking'
import { error } from '@/services/Log'
import type { StandardCart } from '@/types/ShopFront/CheckoutStandards'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'

import dynamic from 'next/dynamic'
import { STORE_MAIN_LOGO_URL, STORE_MINI_LOGO_URL } from '@/data/constants'

const MegaNav = dynamic(import('@csc/dls/MegaNav'))

const UserNavigation = dynamic(import('./UserNavigation'))
const SearchBox = dynamic(import('./SearchBox'))
const CartPreview = dynamic(import('./CartPreview'))
const MyProfile = dynamic(import('./MyProfile'))

// Extracted  apply from '@csc/csc-sdk'.FP to avoid the import cost of 178 KB
// Apply (take a function then the arg and run it)
// export const apply = f => x => f(x)
const apply = <A, B>(f: (x: A) => B) => (x: A): B => f(x)

interface NavbarProps {
  user: GraphQlCustomer | null,
  cart: StandardCart | null,
  pageType: string,
  megaNav: IMeganav,
  onUpdateCart: React.Dispatch<React.SetStateAction<StandardCart | null>>,
}
const Navbar: React.FC<NavbarProps> = ({
  cart,
  onUpdateCart,
  pageType,
  megaNav,
  user,
}) => {
  useListrakCartTracking(cart)
  const [forceMobileExpandedState, setForceMobileExpandedState] = useState(false)
  const [searchBarQuery, setSearchBarQuery] = useState('')

  useEffect(() => {
    allPromisesWithRetries(() => [import('@/services/ExtendWarranty/index')])
      .then(([{ default: ExtendWarranty }]) => ExtendWarranty.initXMiniCart())
      .catch(error)
  }, [])

  useEffect(() => {
    setForceMobileExpandedState(!!searchBarQuery.length)
  }, [searchBarQuery])

  // Some old Iphones scroll the screen messing the UX when focusing on the field
  // As a solution we force the UX for 500ms after focusing on it
  const onFocus = () => apply(setForceMobileExpandedState)(true)
  const onBlur = () => apply(setForceMobileExpandedState)(!!searchBarQuery.length)

  return (
    <>
      <div id="USER_NAVIGATION_ID" className="hidden lg:block">
        <UserNavigation
          user={user}
          cart={cart}
          onUpdateCart={onUpdateCart}
          pageType={pageType}
        />
      </div>
      <MegaNav
        desktopBreakpoint="lg"
        logo={(
          <div
            className="mx-auto"
            style={{
              maxWidth: '200px',
              minWidth: '200px',
              width: '200px',
              minHeight: '64px',
              height: '64px',
              maxHeight: '64px',
              backgroundImage: `url(${STORE_MAIN_LOGO_URL})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            }}
          />
        )}
        smallLogo={STORE_MINI_LOGO_URL}
        megaNavItems={megaNav.meganavItems}
        headerLinks={megaNav.headerLinks}
        mobileNavIcons={(
          [
            <ul className="inline-flex items-center" key="mobileNavIcons">
              <MyProfile user={user} />
              <CartPreview
                color="black"
                cart={cart}
                onUpdateCart={onUpdateCart}
                pageType={pageType}
              />
            </ul>,
          ]
        )}
        searchBar={(
          <SearchBox
            query={searchBarQuery}
            setQuery={setSearchBarQuery}
            id="MeganavSearchBox"
            onFocus={onFocus}
            onBlur={onBlur}
          />
        )}
        mobileUserRedirectUrl={user ? '/account' : '/login'}
        mobileUserIconText={user ? `Hi, ${user.firstName}` : 'Log in'}
        forceMobileExpandedState={forceMobileExpandedState}
      />
    </>
  )
}

export default Navbar
